import axios, {
  type AxiosInstance,
  type AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'
import { ref } from 'vue'
import { configReady, getConfig } from '@/config'
import router from '@/router'
import { useErrorStore } from '@/stores/error.store'

/** Session expiry timestamp (Unix seconds) — updated from X-Session-Expires header */
export const sessionExpiresAt = ref<number | null>(null)

// Create axios instance with default baseURL (resolved per-request below —
// see the request interceptor — so no dispatched request can ever race the
// runtime config load and hit this relative default cross-origin).
const apiClient: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store',
    Pragma: 'no-cache',
    'X-Requested-With': 'XMLHttpRequest',
  },
})

/** Call after loadConfig() resolves to set the correct baseURL */
export function initClient() {
  apiClient.defaults.baseURL = getConfig().apiBaseUrl
}

// --- Request interceptor: guarantee baseURL readiness before dispatch ---
// Every request awaits the runtime config before it goes out, then stamps
// the resolved `apiBaseUrl` directly onto that request's config. This
// eliminates the relative-default window entirely — it no longer matters
// whether `initClient()` has run yet, or whether some future caller bypasses
// `main.ts`'s bootstrap ordering.
apiClient.interceptors.request.use(async (config) => {
  await configReady()
  config.baseURL = getConfig().apiBaseUrl
  return config
})

// --- Refresh-queue state ---
let isRefreshing = false
let failedQueue: Array<{
  resolve: (value: AxiosResponse) => void
  reject: (reason: unknown) => void
  config: InternalAxiosRequestConfig
}> = []

function processQueue(error: unknown) {
  for (const entry of failedQueue) {
    if (error) {
      entry.reject(SESSION_EXPIRED)
    } else {
      apiClient.request(entry.config).then(entry.resolve, entry.reject)
    }
  }
  failedQueue = []
}

/** Sentinel error — components can check `error.isSessionExpired` to silently ignore */
class SessionExpiredError extends Error {
  isSessionExpired = true
  constructor() {
    super('Session expired')
  }
}

function dispatchSessionExpired() {
  console.debug('[client] dispatching session:expired')
  window.dispatchEvent(new CustomEvent('session:expired'))
}

const SESSION_EXPIRED = new SessionExpiredError()

// --- Response interceptor ---
apiClient.interceptors.response.use(
  (response) => {
    // Update session expiry from header on every successful response
    const expiresHeader = response.headers['x-session-expires']
    if (expiresHeader) {
      sessionExpiresAt.value = Number(expiresHeader)
      console.debug(
        '[client] session expires at:',
        new Date(Number(expiresHeader) * 1000).toLocaleTimeString(),
      )
    }
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retried?: boolean }

    if (error.response?.status === 401) {
      // If the refresh endpoint itself failed, session is truly expired
      const requestUrl = originalRequest?.url || ''
      if (requestUrl.includes('/auth/refresh')) {
        dispatchSessionExpired()
        return Promise.reject(SESSION_EXPIRED)
      }

      // If already retried once, give up
      if (originalRequest?._retried) {
        dispatchSessionExpired()
        return Promise.reject(SESSION_EXPIRED)
      }

      // If another request is already refreshing, queue this one
      if (isRefreshing) {
        return new Promise<AxiosResponse>((resolve, reject) => {
          failedQueue.push({ resolve, reject, config: originalRequest })
        })
      }

      // Attempt refresh
      isRefreshing = true
      originalRequest._retried = true

      try {
        const baseUrl = getConfig().apiBaseUrl
        const refreshResponse = await axios.post(baseUrl + '/auth/refresh', null, {
          withCredentials: true,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        })

        // Update session expiry from refresh response
        const expiresHeader = refreshResponse.headers['x-session-expires']
        if (expiresHeader) {
          sessionExpiresAt.value = Number(expiresHeader)
        }

        isRefreshing = false
        processQueue(null)

        // Retry the original request
        return apiClient.request(originalRequest)
      } catch (refreshError) {
        isRefreshing = false
        processQueue(refreshError)
        dispatchSessionExpired()
        return Promise.reject(SESSION_EXPIRED)
      }
    }

    if (error.response?.status === 403) {
      console.error('Insufficient permissions')
    }

    // 5xx surfaces — capture the server envelope into the error store
    // and route to the styled `/500` view. Axios still rejects below so
    // any awaiting caller can run its own cleanup. Pinia / router may
    // not yet be initialised on early bootstrap requests — swallow that
    // case so we don't cascade a second error on top of the first.
    //
    // EXCEPTION: a 503 carrying a structured `E_*` detail is an
    // intentional service envelope (e.g. `E_INTEGRATION_NOT_CONFIGURED`
    // from Gmail/Calendar, `E_DASHBOARD_DISABLED`) — the calling views
    // render their own graceful states ("Integrazione non configurata"),
    // so it must reach them instead of hijacking navigation to /500.
    // Genuine 500/502/504 (and bare proxy 503s) keep the redirect.
    const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail
    const isExpectedServiceEnvelope =
      error.response?.status === 503 && typeof detail === 'string' && detail.startsWith('E_')
    if (error.response && error.response.status >= 500 && !isExpectedServiceEnvelope) {
      console.error('Server error:', error.response.data)
      try {
        const headers = (error.response.headers ?? {}) as Record<string, string | undefined>
        const data = (error.response.data ?? {}) as {
          req_id?: string
          ts?: string
          env?: string
          detail?: string
        }
        const errorStore = useErrorStore()
        errorStore.setFromResponse(
          headers['x-request-id'] || data.req_id || '',
          data.ts || new Date().toISOString(),
          data.env || '',
          data.detail || 'Internal server error',
        )
        // Don't await — let axios' rejection continue to propagate.
        void router.push('/500')
      } catch {
        // Pinia or router not initialised yet; swallow and let the
        // caller handle the original rejection on its own.
      }
    }

    return Promise.reject(error)
  },
)

export default apiClient
